import { Source } from "trader.ts/types/source";
import { Field, Parsing, selectBox } from "trader.ts/ui.forms";
import { isEqual } from "./isequal";
import { isNil } from "./istype";

export async function selectBoxNullable<T>(opts: {
  initial: T | null | undefined;
  values: T[];
  show: (t: T) => string;
  label?: string;
  style?: string;
  class?: string;
  saveAndLoadInitialValToLocalStorage?: string;
  isDisabled?: (t: T) => boolean;
}): Promise<Field<T | null>> {
  const isDisabled = opts.isDisabled;
  const normalField = await selectBox<
    { tag: "none" } | { tag: "item"; item: T }
  >({
    ...opts,
    initial: map2(opts.initial || null),
    values: [null, ...opts.values].map(map2),
    show: (t) => (t.tag === "none" ? "" : opts.show(t.item)),
    isDisabled: !isNil(isDisabled)
      ? (t) => (t.tag === "none" ? false : isDisabled(t.item))
      : undefined,
  });

  const mappedSource: Source<Parsing<T | null>> = new Source({
    tag: "initial",
  });

  function map1(t: { tag: "none" } | { tag: "item"; item: T }): T | null {
    if (t.tag === "none") {
      return null;
    } else {
      return t.item;
    }
  }

  function map2(t: T | null): { tag: "none" } | { tag: "item"; item: T } {
    if (t === null) {
      return { tag: "none" };
    } else {
      return { tag: "item", item: t };
    }
  }

  return {
    ...normalField,
    render: () => {
      forwardParsingSources(
        { from: normalField.s, to: mappedSource, runInitial: true },
        map1,
      );
      forwardParsingSources(
        { from: mappedSource, to: normalField.s, runInitial: false },
        map2,
      );
      return normalField.render();
    },
    s: mappedSource,
  };
}

function forwardParsingSources<T, U>(
  opts: {
    from: Source<Parsing<T>>;
    to: Source<Parsing<U>>;
    runInitial: boolean;
  },
  doMap: (t: T) => U,
) {
  function run(fromVal: Parsing<T>) {
    const currentTo = opts.to.get();
    if (fromVal.tag === "parsed") {
      const mapped = doMap(fromVal.parsed);
      if (currentTo.tag !== "parsed" || !isEqual(currentTo.parsed, mapped)) {
        opts.to.set({ tag: "parsed", parsed: mapped });
      }
    } else {
      if (!isEqual(currentTo, fromVal)) {
        opts.to.set(fromVal);
      }
    }
  }
  opts.from.observe(run);
  if (opts.runInitial === true) {
    run(opts.from.get());
  }
}
